import {
  Box,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import colors from "../../assets/theme/colors";
import { getChildAccentTheme } from "../Dashboard/shared/childAccentTheme";
import {
  getChildAllergySummary,
  getChildCareFocusLabel,
  getChildDiagnosisSummary,
  getChildEmergencyContactSummary,
} from "./profileSwitchMenuData";

const SnapshotRow = ({ label, children, align = "start" }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: "92px minmax(0, 1fr)", gap: 1.2, alignItems: align }}>
    <Typography sx={{ fontSize: "0.72rem", fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: colors.landing.textMuted }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const DashboardProfileSwitchMenu = ({
  anchorEl,
  open,
  onClose,
  activeProfileDraftDisplay,
  dashboardActiveChild,
  dashboardDisplayChild,
  dashboardDisplayAccent,
  childrenWithAccess = [],
  onEditProfile,
  onSelectChild,
  onAddChild,
  renderChildAvatar,
}) => {
  const careFocusLabel = getChildCareFocusLabel(dashboardDisplayChild || {});
  const diagnosisSummary = getChildDiagnosisSummary(dashboardDisplayChild || {});
  const allergySummary = getChildAllergySummary(dashboardDisplayChild || {});
  const emergencyContact = getChildEmergencyContactSummary(dashboardDisplayChild || {});
  const showSnapshot = !activeProfileDraftDisplay?.isDraftProfile
    && (diagnosisSummary || allergySummary || emergencyContact);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{
        sx: {
          mt: 1,
          width: { xs: "calc(100vw - 32px)", sm: 330 },
          maxWidth: 360,
          borderRadius: "22px",
          border: `1px solid ${colors.landing.borderLight}`,
          boxShadow: `0 24px 60px ${colors.landing.shadowPanel}`,
          bgcolor: "rgba(255,255,255,0.98)",
          backdropFilter: "blur(16px)",
          overflow: "hidden",
        },
      }}
    >
      {dashboardDisplayChild ? (
        <Box
          sx={{
            p: 1.65,
            bgcolor: dashboardDisplayAccent.soft,
            borderBottom: `1px solid ${alpha(dashboardDisplayAccent.primary, 0.12)}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.35 }}>
            {renderChildAvatar(dashboardDisplayChild, {
              accent: dashboardDisplayAccent,
              size: 60,
              fontSize: "1.05rem",
              sx: {
                width: 60,
                height: 60,
                border: `3px solid ${colors.landing.surface}`,
                outline: `3px solid ${dashboardDisplayAccent.primary}`,
              },
            })}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, justifyContent: "space-between" }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, color: colors.landing.heroText, lineHeight: 1.05 }}>
                    {dashboardDisplayChild.name || "Profile"}
                  </Typography>
                  <Typography sx={{ mt: 0.25, fontSize: "0.9rem", color: colors.landing.textMuted, lineHeight: 1.15 }}>
                    {careFocusLabel}
                  </Typography>
                </Box>
                {!activeProfileDraftDisplay?.isDraftProfile ? (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.45,
                      px: 1,
                      py: 0.45,
                      borderRadius: 999,
                      bgcolor: dashboardDisplayAccent.primary,
                      color: "#fff",
                      fontSize: "0.72rem",
                      fontWeight: 900,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      flexShrink: 0,
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 15 }} />
                    Current
                  </Box>
                ) : null}
              </Box>
            </Box>
          </Box>

          {showSnapshot ? (
            <Box sx={{ mt: 1.45, display: "grid", gap: 1.1 }}>
              {diagnosisSummary ? (
                <SnapshotRow label="Care focus">
                  <Typography sx={{ fontSize: "0.94rem", fontWeight: 800, color: colors.landing.heroText, lineHeight: 1.28 }}>
                    {diagnosisSummary}
                  </Typography>
                </SnapshotRow>
              ) : null}
              {allergySummary ? (
                <SnapshotRow label="Allergies" align="center">
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.55,
                      width: "fit-content",
                      maxWidth: "100%",
                      px: 1.05,
                      py: 0.45,
                      borderRadius: 999,
                      border: `1px solid ${alpha(colors.semantic.error || "#A94438", 0.25)}`,
                      bgcolor: alpha(colors.semantic.error || "#A94438", 0.08),
                      color: colors.semantic.error || "#8F2F24",
                      fontSize: "0.86rem",
                      fontWeight: 850,
                      lineHeight: 1.1,
                    }}
                  >
                    <WarningAmberOutlinedIcon sx={{ fontSize: 17, flexShrink: 0 }} />
                    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {allergySummary}
                    </Box>
                  </Box>
                </SnapshotRow>
              ) : null}
              {emergencyContact ? (
                <SnapshotRow label="Emergency">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.92rem", fontWeight: 800, color: colors.landing.heroText, lineHeight: 1.2 }}>
                      {emergencyContact.name}
                    </Typography>
                    {emergencyContact.phone && emergencyContact.phoneHref ? (
                      <Box
                        component="a"
                        href={emergencyContact.phoneHref}
                        onClick={(event) => event.stopPropagation()}
                        sx={{
                          mt: 0.45,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: dashboardDisplayAccent.primary,
                          fontSize: "0.9rem",
                          fontWeight: 850,
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        <PhoneOutlinedIcon sx={{ fontSize: 17 }} />
                        {emergencyContact.phone}
                      </Box>
                    ) : null}
                  </Box>
                </SnapshotRow>
              ) : null}
            </Box>
          ) : null}
        </Box>
      ) : null}

      <MenuItem
        onClick={onEditProfile}
        sx={{
          gap: 1.1,
          py: 1.35,
          px: 1.5,
          minHeight: 54,
          bgcolor: alpha(colors.brand.ink, 0.08),
          borderBottom: `4px solid ${dashboardDisplayAccent.primary}`,
          "&:hover": {
            bgcolor: alpha(colors.brand.ink, 0.12),
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 34 }}>
          <DescriptionOutlinedIcon sx={{ fontSize: 21, color: colors.brand.navy }} />
        </ListItemIcon>
        <Typography sx={{ fontSize: "1rem", fontWeight: 900, color: colors.brand.navy }}>
          {activeProfileDraftDisplay?.isDraftProfile ? "Finish new profile" : `View & edit ${dashboardActiveChild?.name || "profile"}'s profile`}
        </Typography>
      </MenuItem>

      <Box sx={{ px: 1.5, pt: 1, pb: 0.4 }}>
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 850, letterSpacing: "0.11em", textTransform: "uppercase", color: colors.landing.textMuted }}>
          Switch to someone else
        </Typography>
      </Box>

      {childrenWithAccess.filter((child) => child.id !== dashboardDisplayChild?.id).map((child) => {
        const childAccent = getChildAccentTheme(child, { children: childrenWithAccess });
        const childCareFocusLabel = getChildCareFocusLabel(child);

        return (
          <MenuItem
            key={child.id}
            onClick={() => onSelectChild(child.id)}
            sx={{ gap: 1.25, py: 1.15, px: 1.5, minHeight: 58 }}
          >
            {renderChildAvatar(child, {
              accent: childAccent,
              size: 38,
              fontSize: "0.86rem",
            })}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: "0.98rem", fontWeight: 850, color: colors.landing.heroText, lineHeight: 1.1 }}>
                {child.name}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.landing.textMuted, fontWeight: 650 }}>
                {childCareFocusLabel}
              </Typography>
            </Box>
            <ChevronRightIcon sx={{ fontSize: 22, color: colors.landing.textMuted, flexShrink: 0 }} />
          </MenuItem>
        );
      })}
      <MenuItem
        onClick={onAddChild}
        sx={{ gap: 1.25, py: 1.25, px: 1.5, minHeight: 48 }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          <PersonAddAlt1OutlinedIcon sx={{ fontSize: 18, color: colors.brand.ink }} />
        </ListItemIcon>
        Add a new person to track
      </MenuItem>
    </Menu>
  );
};

export default DashboardProfileSwitchMenu;
